import { useTranslation } from "react-i18next";
import { Tree, TreeNode } from "react-organizational-chart";

import { Button, Flex, Row, Space, Spin } from "antd";

import { useSelector } from "react-redux";
import styles from "../styles/Employee.module.css";

import { ReactComponent as DownloadIcon } from "../../../assets/icon/downloadIcon.svg";
import { useEffect, useRef, useState } from "react";
import { OrgChart } from "d3-org-chart";
import * as d3 from "d3"
import { toJpeg } from "html-to-image";

function EmployeeEmploymentOrganizationStructure({
  isLoading = false,
  onDownload = () => { },
  graphRef = null,
}) {
  const { t, i18n } = useTranslation();
  const currentCompany = useSelector((state) => state.company.companyInformation)
  const organizationStructure = useSelector((state) => state.employee.organizationStructure)
  const [chart, setChart] = useState(null);
  const chartRef = useRef(null);
  const [lang, setLang] = useState("en");

  console.log("org data", organizationStructure);

  function transformData(data, lang) {
    const result = [{
      id: 'root', preventExpand: true, parentId: null
      , name: currentCompany?.['companyName' + (lang === 'en' ? "En" : "")], position: ""
      , image: 'https://bumbeishvili.github.io/avatars/avatars/portrait12.png',
      email: 'SKING', phone_number: '515.123.4567', hire_date: '2003-07-16T19:00:00.000Z'
    }];

    function traverse(node, parentId = null) {
      const transformedNode = {
        id: node.organizationId || node.id,
        parentId: parentId || 'root',
        name: node?.['organizationName' + (lang == 'en' ? "En" : "")] || node.name,
        position: node.organizationLevel?.name || 'Unknown Position',
        image: node.image || '',
        email: node.email || '',
        phone_number: node.phone_number || '',
        hire_date: node.hire_date || '',
        employeeDepartment: node.employeeDepartment || '',
        preventExpand: true
      };

      result.push(transformedNode);

      if (node.children && node.children.length > 0) {
        node.children.forEach(child => traverse(child, transformedNode.id));
      }
    }
    data.map(o => traverse(o))
    return result;
  }

  const downloadImage = async () => {
    const chartElement = document.getElementById('employmentOrganizationStructure');

    if (!chartElement) {
      console.error('Element with id "employmentOrganizationStructure" not found');
      return;
    }

    const originalWidth = chartElement.style.width;
    const originalHeight = chartElement.style.height;

    chartElement.style.width = `${chartElement.scrollWidth}px`;
    chartElement.style.height = `${chartElement.scrollHeight}px`;

    const scale = 10;
    try {
      const dataUrl = await toJpeg(chartElement, {
        width: chartElement.scrollWidth * scale,
        height: chartElement.scrollHeight * scale,
        backgroundColor: "white",
        style: {
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          width: `${chartElement.scrollWidth}px`,
          height: `${chartElement.scrollHeight}px`,
        },
      });

      chartElement.style.width = originalWidth;
      chartElement.style.height = originalHeight;

      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = 'organizational-chart.jpg';
      link.click();

    } catch (error) {
      console.error('Could not generate image', error);
    }
  };

  useEffect(() => {
    const chartContainer = chartRef.current;

    if (!chartContainer) {
      console.error("Chart container is not available.");
      return;
    }

    const orgChart = new OrgChart()
      .nodeHeight((d) => 50 + 25)
      .nodeWidth((d) => 350 + 2)
      .childrenMargin((d) => 150)
      .compactMarginBetween((d) => 200)
      .compactMarginPair((d) => 200)
      .neighbourMargin((a, b) => 200)
      .buttonContent(() => { })
      .nodeContent(function (d) {
        const color = d.data?.employeeDepartment === 'Y' ? 'yellow' : '#FFFFFF';
        return `
            <div id='node-na' style="width:${d.width}px;height:${d.height}px;padding-left:1px;padding-right:1px;">
              <div style=";background-color:${color};margin-left:-1px;width:${d.width - 2}px;border-radius:100px;border: 1px solid black ;padding-left :20px;padding-right :20px;">
                <div style="min-height:${d.height}px;align-content:center;font-size:1.3vw;color:#08011E;text-align:center ;white-space: normal; word-wrap: break-word; overflow: hidden; text-overflow: ellipsis;">${d.data?.name}</div>
              </div>
            </div>`;
      })
      .container(chartContainer)
      .data(transformData(organizationStructure, lang))
      .render();

    const svg = d3.select(chartContainer).select("svg");

    if (svg) {
      const defs = svg.append("defs");

      svg.selectAll(".node")
        .style("cursor", "default");

    }
    orgChart?.expandAll()
    orgChart?.compact?.(false).render();
    orgChart?.nodeButtonWidth((node) => node.data?.preventExpand ? 0 : 20)
      .nodeButtonHeight((node) => node.data?.preventExpand ? 0 : 20);
    const rootNodeId = organizationStructure[0]?.id;
    if (rootNodeId) {
      orgChart.setCentered(rootNodeId);
    }

    setChart(orgChart);
    return () => {
      if (chart) {
        chart?.destroy?.();
      }
    };
  }, [organizationStructure, lang]);

  useEffect(() => {
    setLang(i18n.language);
  }, [i18n.language, setLang]);

  useEffect(() => {
    if (graphRef && chart) {
      graphRef.current = chart;
    }
  }, [chart, graphRef]);

  const handleDownloadClick = async () => {
    try {
      let res = await chart?.compact?.(false).render().fit().render();
      if (res) {
        await new Promise(resolve => setTimeout(resolve, 3000));
        downloadImage();
      }
    } catch (error) {
      console.error('Error during download:', error);
    }
  };

  return (
    <Space
      style={{ width: "100%", overflow: "auto" }}
      size={24}
      direction="vertical"
    >
      <Row justify="end">
        <Button type="default" disabled={isLoading} onClick={handleDownloadClick}
          style={{ padding: "8px 16px", height: "44px", borderRadius: "8px" }}
        >
          <Flex align="center" justify="center" gap={8}>
            <DownloadIcon />
            <span style={{ color: "#667085", fontWeight: "bold" }}>{t('common.download')}</span>
          </Flex>
        </Button>
      </Row>

      <div className={styles.stylesNode2}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px', gap: "12px" }}>
          <Button onClick={() => {
            chart.nodeHeight(() => 125 + 25);
            chart.layout('left').render().fit()
          }}>
            {t("og_chart.left")}
          </Button>
          <Button onClick={() => {
            chart.nodeHeight(() => 50 + 25);
            chart.layout('top').render().fit()
          }}>
            {t("og_chart.top")}
          </Button>
        </div>
        <div id="employmentOrganizationStructure" style={{ width: "100%" }} ref={chartRef} className="chart-container"></div>
      </div>
    </Space>
  );
}

export default EmployeeEmploymentOrganizationStructure;
